import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { TextField } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useTheme } from "@mui/material/styles";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useUpdatePasswordMutation } from "../../api/userApi";
import { useToast } from "../../components/ui/Toast";
import { AccountHeader, FixedFooter, RowGroup } from "./components/AccountSoft";
import { greenButtonSx } from "./actionStyles";
import "./darkSurface.css";

/**
 * Şifre Değiştir (mobildeki ayrı sayfa): mevcut, yeni ve tekrar; kaydet altta
 * sabit, yeşil. Kayıttan sonra hesap sayfasına dönülür.
 */
export default function AccountPasswordPage() {
  const { t } = useScriptMode();
  const toast = useToast();
  const navigate = useNavigate();
  const mode = useTheme().palette.mode;

  const schema = yup.object({
    currentPassword: yup.string().required(t("validation_required")),
    newPassword: yup.string().min(8, t("validation_password_min")).required(t("validation_required")),
    newPasswordConfirm: yup
      .string()
      .oneOf([yup.ref("newPassword")], t("validation_password_match"))
      .required(t("validation_required")),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { currentPassword: "", newPassword: "", newPasswordConfirm: "" },
  });

  const [updatePassword, { isLoading }] = useUpdatePasswordMutation();

  const onSubmit = async (values) => {
    const { newPasswordConfirm, ...payload } = values;
    const result = await updatePassword(payload);
    if (result.error) {
      toast.error(result.error.message || t("password_change_error"));
      return;
    }
    toast.success(t("password_change_success"));
    navigate("/uygulama/hesap");
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="tk-dark w-full max-w-3xl mx-auto flex flex-col gap-4"
    >
      <AccountHeader title={t("Şifre Değiştir")} backTo="/uygulama/hesap" />
      <RowGroup>
        <div className="p-4 flex flex-col gap-4">
          <TextField
            label={t("Mevcut şifre")}
            type="password"
            autoComplete="current-password"
            {...register("currentPassword")}
            error={Boolean(errors.currentPassword)}
            helperText={errors.currentPassword?.message}
          />
          <TextField
            label={t("Yeni şifre")}
            type="password"
            autoComplete="new-password"
            {...register("newPassword")}
            error={Boolean(errors.newPassword)}
            helperText={errors.newPassword?.message}
          />
          <TextField
            label={t("password_confirm")}
            type="password"
            autoComplete="new-password"
            {...register("newPasswordConfirm")}
            error={Boolean(errors.newPasswordConfirm)}
            helperText={errors.newPasswordConfirm?.message}
          />
        </div>
      </RowGroup>
      <FixedFooter>
        <LoadingButton
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          loading={isLoading}
          sx={{ ...greenButtonSx(mode), borderRadius: 999 }}
        >
          {t("Şifreyi Güncelle")}
        </LoadingButton>
      </FixedFooter>
    </form>
  );
}
